import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiLock } from 'react-icons/fi';
import { useClient } from '../contexts/ClientContext';
import axios from 'axios';
import { queryClient } from '../lib/queryClient';
import { createForceLogout } from '../utils/forceLogout';

const isLoginRoute = (pathname: string) => {
  return /^\/(?:superadmin|[^/]+)\/login$/.test(pathname);
};

const BlockedUserModal: React.FC = () => {
  const { client, refreshClient } = useClient();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Check if user is blocked
  useEffect(() => {
    if (client?.isBlocked === true && !isLoginRoute(location.pathname)) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [client?.isBlocked, location.pathname]);

  // Auto-refresh DISABLED - only check on mount, not periodically
  // User will be checked when they navigate or when component mounts
  // No automatic polling to prevent unnecessary refreshes

  const handleLogout = async () => {
    // 🔥 CRITICAL: Log manual logout from blocked user modal
    
    setIsLoggingOut(true);
    
    
    // CRITICAL: Set manual logout flag BEFORE clearing data
    const logoutTimestamp = Date.now().toString();
    sessionStorage.setItem('manualLogout', 'true');
    localStorage.setItem('manualLogout', 'true');
    sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
    localStorage.setItem('manualLogoutTime', logoutTimestamp);
    
    // CRITICAL: Set logoutReason so Login page knows to perform cleanup
    sessionStorage.setItem('logoutReason', 'manual-logout');
    
    // CRITICAL: Clear justLoggedIn flag to prevent interceptor from ignoring 401 errors
    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('justLoggedInTime');
    sessionStorage.removeItem('justLoggedInCompany');
    
    try {
      // IMPORTANT: Clear all React Query cache and CRM-related localStorage on logout
      // to ensure next login starts from a clean slate
      // 1) Clear all React Query caches (queries + mutations)
      queryClient.clear();
      
      // 2) Clear all CRM-related auth and company data from localStorage
      // Keep user preferences like rememberEmail, rememberPassword, device_id
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('client');
      localStorage.removeItem('currentCompany');
      localStorage.removeItem('activeTenantId');
      localStorage.removeItem('activeTenantName');
      localStorage.removeItem('activeRole');
      localStorage.removeItem('lastLogin');
      localStorage.removeItem('lastActivity');
      
      // 3) Clear axios default headers
      delete axios.defaults.headers.common.Authorization;
      
      // Get company from pathname
      const pathParts = location.pathname.split('/').filter(Boolean);
      const company = pathParts[0] || 'test';
      
      // Use forceLogout with manual_click reason
      const forceLogout = createForceLogout(queryClient);
      setTimeout(() => {
        forceLogout({
          reason: 'manual_click',
          source: 'BlockedUserModal.handleLogout',
          companySlug: company,
        });
      }, 500);
    } catch (error) {
      // 🔥 CRITICAL: Log error during logout
      console.warn('[FORCE-LOGOUT] Error during BlockedUserModal logout, redirecting anyway', {
        source: 'BlockedUserModal.handleLogout.error',
        reason: 'user-blocked-error-during-logout',
        error: error instanceof Error ? error.message : String(error),
        pathname: location.pathname,
      });
      
      // Even if there's an error, try to redirect
      const pathParts = location.pathname.split('/').filter(Boolean);
      const company = pathParts[0] || 'test';
      window.location.href = `/${company}/login`;
    }
  };

  if (!showModal) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4" 
      dir="rtl"
      onClick={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        // Prevent closing with ESC
        if (e.key === 'Escape') e.preventDefault();
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <FiLock className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          המשתמש נחסם
        </h2>

        {/* Message */}
        <div className="text-center text-gray-700 mb-6 space-y-2">
          <p className="text-base">
            המשתמש שלך נחסם מהמערכת.
          </p>
          <p className="text-sm text-gray-600">
            אין לך הרשאה להמשיך להשתמש במערכת. אנא פנה למנהל המערכת למידע נוסף.
          </p>
        </div>

        {/* Button */}
        <div className="flex justify-center">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all ${
              isLoggingOut
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 hover:shadow-xl'
            }`}
          >
            {isLoggingOut ? 'מתנתק...' : 'הבנתי'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockedUserModal;

